import { Autocomplete, Paper, TextField } from "@mui/material";
import React, { useId, useState } from "react";
import { useEffect } from "react";

export default function MUIAutocomplete({
  inputLabel,
  selectedOption,
  setSelectedOption,
  optionsList,
  multiple,
  required,
  name,
  optionValueKey,
  formatOptionLabel,
  size,
  setCustomSearch,
  disabled,
  autoComplete = "new-password",
}) {
  const [inputValue, setInputValue] = useState("");
  const [dataList, setDataList] = useState([]);
  const inputId = useId();
  const safeInputId = inputId.replace(/:/g, "");
  const autoFillGuardValue =
    autoComplete === "off" ? `new-password-${safeInputId}` : autoComplete;
  const getOptionValue = (option) =>
    option?.[optionValueKey || name];
  const getLabel = (option) => {
    if (!option) return "";
    if (typeof formatOptionLabel === "function") {
      return formatOptionLabel(option);
    }
    const baseLabel = option[name] ?? "";
    const identity = option.cnic || option.email;
    if (name === "name" && identity) {
      return `${baseLabel} (${identity})`;
    }
    return baseLabel;
  };

  useEffect(() => {
    const list = optionsList ?? [];
    // Only exclude already-selected items in multi-select mode.
    // For single-select, the value must stay in `options` or MUI Autocomplete
    // resets the controlled input and the selection appears to clear.
    if (!multiple) {
      setDataList(list);
      return;
    }
    const selected = selectedOption ?? [];
    setDataList(
      list.filter(
        (item) =>
          !selected.some(
            (selectedItem) =>
              getOptionValue(selectedItem) === getOptionValue(item),
          ),
      ),
    );
  }, [optionsList, selectedOption, multiple]);

  const handleChangeInput = (value) => {
    if (setCustomSearch) {
      setCustomSearch(value);
    }
    setInputValue(value);
  };

  let array_required = false;
  if (required) {
    if (multiple && selectedOption.length === 0) {
      array_required = true;
    } else {
      if (!selectedOption) {
        array_required = true;
      }
    }
  }

  return (
    <Autocomplete
      multiple={multiple}
      value={selectedOption}
      disabled={disabled}
      onChange={(event, newValue) => {
        setSelectedOption(newValue);
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        handleChangeInput(newInputValue);
      }}
      className="w-100 mui-select autocomplete-mui"
      PaperComponent={({ children }) => (
        <Paper className="autocomplete-paper">{children}</Paper>
      )}
      size={size ? size : "medium"}
      id={`mui-autocomplete-${safeInputId}`}
      options={dataList}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(option, value) => {
        if (!option || !value) return option === value;
        if (option._id != null && value._id != null) {
          return String(option._id) === String(value._id);
        }
        return getOptionValue(option) === getOptionValue(value);
      }}
      renderInput={(params) => {
        const inputProps = { ...params.inputProps };
        inputProps.autoComplete = autoFillGuardValue;
        inputProps.name = `mui-autocomplete-${safeInputId}`;
        inputProps.id = `mui-autocomplete-input-${safeInputId}`;
        inputProps["data-form-type"] = "other";
        inputProps["data-lpignore"] = "true";
        inputProps["data-1p-ignore"] = "true";
        inputProps.autoCorrect = "off";
        inputProps.autoCapitalize = "none";
        inputProps.spellCheck = false;
        if (array_required) {
          inputProps.required = true;
        }
        return (
          <TextField
            {...params}
            inputProps={inputProps}
            className="form-control"
            label={inputLabel}
            placeholder={inputLabel}
            autoComplete={autoFillGuardValue}
            required={array_required}
          />
        );
      }}
    />
  );
}
